import { useState, type ReactElement } from 'react';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import {
  alertFeedbackStatusOptions,
  MAX_ALERT_COMMENT_LENGTH,
  type AlertFeedbackStatus,
  type GroupedAlert,
} from '@kirin/api-client/contracts/alerts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useToast } from '@/hooks/use-toast';
import {
  useSetAlertStatus,
  useSetAlertComment,
} from '@/hooks/api/useAlertFeedback';
import { formatDateTime } from '@/lib/date-utils';

const STATUS_LABELS: Record<AlertFeedbackStatus, string> = Object.fromEntries(
  alertFeedbackStatusOptions.map(o => [o.value, o.label])
) as Record<AlertFeedbackStatus, string>;

/** Build the "Set by X · <date>" attribution line, or null when unset. */
function attributionLine(
  name: string | null,
  email: string | null,
  at: string | null,
  timeZone: string
): string | null {
  if (!at) return null;
  const who = name ?? email ?? 'a user';
  return `Set by ${who} · ${formatDateTime(at, timeZone)}`;
}

/**
 * Wrapper that stops row-expansion click-through so the interactive control can
 * own its own click/focus semantics (mirrors the Actions column, columns.tsx).
 */
function CellWrapper({
  children,
}: {
  children: React.ReactNode;
}): ReactElement {
  return (
    <div
      className="flex items-center"
      onClick={event => event.stopPropagation()}
      onPointerDown={event => event.stopPropagation()}
    >
      {children}
    </div>
  );
}

/**
 * KIR-4440 Status cell. Owner/manager see an editable dropdown; everyone else
 * sees a read-only badge (the verdict is visible to the whole org). Hover shows
 * who set it and when. Setting a status is information only.
 */
export function AlertStatusCell({
  alert,
  organizationId,
  canEdit,
}: {
  alert: GroupedAlert;
  organizationId: string;
  canEdit: boolean;
}): ReactElement {
  const { toast } = useToast();
  const timeZone = useDisplayTimeZone();
  const mutation = useSetAlertStatus(organizationId);
  const status: AlertFeedbackStatus = alert.feedback?.status ?? 'unverified';
  const tooltip = alert.feedback
    ? attributionLine(
        alert.feedback.statusSetByName,
        alert.feedback.statusSetByEmail,
        alert.feedback.statusSetAt,
        timeZone
      )
    : null;

  const badge = (
    <Badge variant="outline" className="whitespace-nowrap">
      {STATUS_LABELS[status]}
    </Badge>
  );

  const withTooltip = (node: ReactElement): ReactElement =>
    tooltip ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{node}</TooltipTrigger>
          <TooltipContent>{tooltip}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      node
    );

  if (!canEdit) {
    return <CellWrapper>{withTooltip(badge)}</CellWrapper>;
  }

  const onChange = async (value: string): Promise<void> => {
    try {
      await mutation.mutateAsync({
        alertKey: alert.alertKey,
        status: value as AlertFeedbackStatus,
      });
    } catch {
      toast({
        title: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  // The tooltip must wrap the SelectTrigger (a real <button>), NOT the Select
  // root — the Radix Select root renders no DOM node, so a TooltipTrigger asChild
  // around it never attaches (managers/owners would lose the "set by X" hover).
  const trigger = (
    <SelectTrigger className="h-8 w-[140px]" aria-label="Alert status">
      <SelectValue />
    </SelectTrigger>
  );

  return (
    <CellWrapper>
      <Select
        value={status}
        onValueChange={onChange}
        disabled={mutation.isPending}
      >
        {tooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
              <TooltipContent>{tooltip}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          trigger
        )}
        <SelectContent>
          {alertFeedbackStatusOptions.map(option => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </CellWrapper>
  );
}

/**
 * KIR-4440 Comment cell. Any org member (excluding support) can edit via a
 * popover; support sees read-only text. The comment is a single free-text field
 * per alert group. Hover shows the last editor + timestamp.
 */
export function AlertCommentCell({
  alert,
  organizationId,
  canEdit,
}: {
  alert: GroupedAlert;
  organizationId: string;
  canEdit: boolean;
}): ReactElement {
  const { toast } = useToast();
  const timeZone = useDisplayTimeZone();
  const mutation = useSetAlertComment(organizationId);
  const comment = alert.feedback?.comment ?? '';
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(comment);
  const tooltip = alert.feedback
    ? attributionLine(
        alert.feedback.commentSetByName,
        alert.feedback.commentSetByEmail,
        alert.feedback.commentSetAt,
        timeZone
      )
    : null;

  const preview = comment ? (
    <span className="block max-w-[240px] truncate text-sm">{comment}</span>
  ) : (
    <span className="text-sm text-muted-foreground">
      {canEdit ? 'Add comment' : '—'}
    </span>
  );

  const withTooltip = (node: ReactElement): ReactElement =>
    tooltip && comment ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{node}</TooltipTrigger>
          <TooltipContent className="max-w-[320px] whitespace-pre-wrap">
            {comment}
            {'\n'}
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      node
    );

  if (!canEdit) {
    return <CellWrapper>{withTooltip(preview)}</CellWrapper>;
  }

  const onSave = async (): Promise<void> => {
    try {
      await mutation.mutateAsync({ alertKey: alert.alertKey, comment: draft });
      setOpen(false);
    } catch {
      toast({
        title: 'Failed to save comment',
        variant: 'destructive',
      });
    }
  };

  return (
    <CellWrapper>
      <Popover
        open={open}
        onOpenChange={next => {
          if (next) setDraft(comment);
          setOpen(next);
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className="w-full min-w-[120px] cursor-text text-left"
            aria-label="Edit comment"
          >
            {withTooltip(preview)}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80" align="start">
          <Textarea
            value={draft}
            onChange={event => setDraft(event.target.value)}
            maxLength={MAX_ALERT_COMMENT_LENGTH}
            rows={4}
            placeholder="Add context — e.g. this is our test data"
            aria-label="Comment text"
          />
          <div className="mt-2 flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={onSave} disabled={mutation.isPending}>
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </CellWrapper>
  );
}
